/**
 * @fileoverview Product Card Custom Element - Production Version
 * Focuses on core swatch functionality with clean, production-ready code
 */

class ProductCardElement extends HTMLElement {
  constructor() {
    super();
    this.productData = new Map();
    this.isInitialized = false;
    this.updateFunction = null;
    this.priceLabel = '';
    this.swatchDataClass = '.color-selector__radio';

    this.CONSTANTS = window.PRODUCT_CARD_CONSTANTS || this.getDefaultConstants();

    this.translations = window.productTileStrings || {
      soldOut: 'Sold Out',
      preorder: 'Pre-order',
      earlyAccess: 'Early Access',
      comingSoon: 'Coming Soon',
      noPromo: 'Excluded from Promotions',
      onlineExclusive: 'Online Exclusive',
      new: 'New',
      lowStock: 'Low Stock',
      lastChance: 'Last Chance',
      membersOnlyPrice: 'Members Only Price',
      salePrice: 'Sale Price',
      regularPrice: 'Regular Price',
    };

    // Initialize cached data maps
    this.logosMap = null;
    this.highlightMap = null;
  }

  /**
   * Get default constants if global ones aren't available
   */
  getDefaultConstants() {
    return {
      SELECTORS: {
        COLOR_SELECTOR: '.color-selector',
        PRODUCT_TILE: '.c-product-tile',
        PRODUCT_LABEL: '.c-product-tile__product-label',
        PRICE_LABEL: '.c-product-tile__price-label',
        PRICE_LABEL_CONTAINER: '.c-product-tile-info__item--price',
        PRODUCT_IMAGE: '.image-button__image',
        PRODUCT_TITLE: '.c-product-tile__product-name span.fixed-utility-s',
        PRODUCT_LINK: '.image-button__link.c-product-tile-decorator__content-wrapper',
        PRICE_CONTAINER: '.c-product-price',
        PRICE_ELEMENT: '.c-product-price__formatted-price',
        DISCOUNTED_PRICE: '.c-product-price__formatted-price--discounted',
        LIST_ITEM: '.o-style-option__list-item',
      },
      CLASSES: {
        CHECKED: 'is-checked',
        HAS_DISCOUNT: 'c-product-price--has-discount',
        LABEL_CONTENT: 'c-product-tile__product-label-content fixed-utility-s',
        DISCOUNTED_PRICE:
          'c-product-price__formatted-price c-product-price__formatted-price--discounted fixed-utility-s one-liner-height-trim',
        SR_ONLY: 'sr-only',
      },
      COLORS: {
        URGENCY: 'c-product-text-color--urgency',
        REGULAR: 'c-product-text-color--regular',
        LOYALTY: 'c-product-text-color--loyalty',
      },
      THRESHOLDS: {
        NEW_PRODUCT_DAYS: 60,
      },
    };
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback() {
    // Wait for global product data to be available
    if (window.globalProductData) {
      this.initialize();
    } else {
      this.waitForServices();
    }
  }

  /**
   * Wait for required services to be available
   */
  waitForServices() {
    const checkServices = () => {
      if (window.globalProductData) {
        this.initialize();
      } else {
        setTimeout(checkServices, 50);
      }
    };

    checkServices();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkServices);
    }
  }

  /**
   * Initialize the product card element
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      if (!window.globalProductData) {
        this.setupBasicFunctionality();
        this.isInitialized = true;
        return;
      }

      this.setupEventListeners();
      this.setupAccessibility();
      this.observeInitialLabels();
      await this.loadCompanionProducts();

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize product card:', error);
      this.setupBasicFunctionality();
      this.isInitialized = true;
    }
  }

  /**
   * Observe initial labels from Liquid
   */
  observeInitialLabels() {
    // Labels are handled by Liquid template initially
    // JavaScript only updates them during swatch interactions
  }

  /**
   * Set up basic functionality when full services aren't available
   */
  setupBasicFunctionality() {
    this.setupAccessibility();
    this.observeInitialLabels();
    this.applySwatchVisibility();

    this.addEventListener('click', (event) => {
      const swatch = event.target.closest(this.swatchDataClass);
      if (swatch) {
        this.updateSwatchState(swatch);
      }
    });
  }

  /**
   * Set up event listeners for swatch interactions
   */
  setupEventListeners() {
    this.updateFunction = this.debounce((productData) => {
      this.updateCard(productData);
    }, 100);

    this.addEventListener('change', this.handleSwatchClick.bind(this));
    this.addEventListener('click', this.handleSwatchClick.bind(this));
    this.addEventListener('keydown', this.handleSwatchKeydown.bind(this));
  }

  /**
   * Handle swatch click events
   */
  handleSwatchClick(event) {
    const swatch = event.target.closest(this.swatchDataClass);
    if (!swatch) return;

    event.preventDefault();
    this.selectSwatch(swatch);
  }

  /**
   * Handle swatch keyboard events
   */
  handleSwatchKeydown(event) {
    const swatch = event.target.closest(this.swatchDataClass);
    if (!swatch) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectSwatch(swatch);
    }
  }

  /**
   * Select a swatch and update the product card
   */
  async selectSwatch(swatch) {
    if (swatch.classList.contains('is-checked')) {
      return;
    }

    if (swatch.classList.contains('is-unavailable') || swatch.hasAttribute('disabled')) {
      return;
    }

    const productId = swatch.dataset.productId;
    if (!productId) {
      return;
    }

    this.updateSwatchState(swatch);

    let productData = this.productData.get(productId);

    // If product data not in cache, try to fetch it from globalProductData
    if (!productData && window.globalProductData) {
      try {
        await window.globalProductData.initialize();
        productData = window.globalProductData.getProduct(productId);
        if (productData) {
          this.productData.set(productId, productData);
        }
      } catch (error) {
        console.error('Failed to fetch product data:', error);
      }
    }

    if (productData) {
      this.updateFunction(productData);
    }
  }

  /**
   * Update swatch selection state with accessibility
   */
  updateSwatchState(activeSwatch) {
    const swatches = this.querySelectorAll(this.swatchDataClass);
    swatches.forEach((swatch) => {
      swatch.classList.remove('is-checked');
      swatch.removeAttribute('checked');
      swatch.closest('.color-selector')?.classList.remove('is-checked');
      swatch.closest('.o-style-option__list-item')?.classList.remove('is-checked');
    });

    activeSwatch.classList.add('is-checked');
    activeSwatch.setAttribute('checked', 'true');
    activeSwatch.closest('.color-selector')?.classList.add('is-checked');
    activeSwatch.closest('.o-style-option__list-item')?.classList.add('is-checked');
  }

  /**
   * Load companion products from global cache
   */
  async loadCompanionProducts() {
    const swatches = this.querySelectorAll(this.swatchDataClass);
    if (swatches.length === 0) {
      return;
    }

    try {
      await window.globalProductData.initialize();

      // Load companion product data (which may include the original product)
      swatches.forEach((swatch) => {
        const productId = swatch.dataset.productId;
        if (productId) {
          const productData = window.globalProductData.getProduct(productId);
          if (productData) {
            this.productData.set(productId, productData);
          } else {
            // delete all the HTML elements till the <li> element to keep consistency
            swatch.parentElement.parentElement.parentElement.remove();
          }
        }
      });

      // Only fetch original product separately if it's not already in the cache
      // (it might already be included in companion products)
      const originalProductId = this.dataset.productId;
      if (originalProductId && !this.productData.has(originalProductId)) {
        const originalProductData = window.globalProductData.getProduct(originalProductId);
        if (originalProductData) {
          this.productData.set(originalProductId, originalProductData);
        }
      }

      this.applySwatchVisibility();
    } catch (error) {
      console.error('Failed to load companion products:', error);
    }
  }

  /**
   * Apply swatch visibility.
   * Ports use-processed-swatches.ts logic exactly:
   *   - isAvailable via window.utilities.cardHelpers.isInStock
   *   - swatchesToShow = available + checked-even-if-unavailable
   *   - fallback when nothing available: show first swatch only
   *   - first MAX_VISIBLE (3) of swatchesToShow are shown; rest hidden
   *   - remaining = swatchesToShow.length - MAX_VISIBLE
   * Plus user requirement: checked swatch is always moved to position 0.
   */
  applySwatchVisibility() {
    const MAX_VISIBLE = 3;
    const ul = this.querySelector('.c-product-option__list');
    if (!ul) return;

    // Remove any previously created JS badge so we start clean
    ul.querySelector('.o-style-option__option--is-decorative')?.closest('.o-style-option__list-item')?.remove();

    // All <li>s that contain a swatch radio input
    const swatchItems = Array.from(ul.querySelectorAll(':scope > .o-style-option__list-item')).filter((li) =>
      li.querySelector(this.swatchDataClass),
    );
    if (swatchItems.length === 0) return;

    // Mirror extractNumericId from bloomreach/components/product-card/utils/index.ts
    const extractNumericId = (id) => {
      if (!id) return '';
      const gid = String(id).match(/gid:\/\/shopify\/Product\/(\d+)/i);
      if (gid) return gid[1];
      const numeric = String(id).match(/^(\d+)/);
      if (numeric) return numeric[1];
      return String(id);
    };

    const currentNumericId = extractNumericId(this.dataset.productId);

    // Mirror use-processed-swatches.ts: build enriched list with isAvailable + isChecked
    const swatchesWithData = swatchItems.map((li) => {
      const input = li.querySelector(this.swatchDataClass);
      const productId = input?.dataset.productId || '';
      const productData = this.productData.get(productId);
      // ?? true mirrors Preact's (utilities?.cardHelpers?.isInStock?.(productData) ?? true)
      const isAvailable = productData ? (window.utilities?.cardHelpers?.isInStock?.(productData) ?? true) : true;
      const isChecked = extractNumericId(productId) === currentNumericId;
      return { li, input, isAvailable, isChecked };
    });

    const availableSwatches = swatchesWithData.filter((s) => s.isAvailable);

    // Mirror use-processed-swatches.ts swatchesToShow logic
    let swatchesToShow =
      availableSwatches.length > 0
        ? swatchesWithData.filter((s) => s.isAvailable || s.isChecked)
        : swatchesWithData.slice(0, 1);

    // User requirement: checked swatch always comes first
    const checkedIdx = swatchesToShow.findIndex((s) => s.isChecked);
    if (checkedIdx > 0) {
      const [checked] = swatchesToShow.splice(checkedIdx, 1);
      swatchesToShow = [checked, ...swatchesToShow];
      // Also reorder in the DOM
      ul.insertBefore(checked.li, swatchesToShow[1].li);
    }

    const visibleSet = new Set(swatchesToShow.slice(0, MAX_VISIBLE).map((s) => s.li));
    // Mirror: remaining = max(0, swatchesToShow.length - MAX_VISIBLE)
    const remaining = Math.max(0, swatchesToShow.length - MAX_VISIBLE);

    // Apply hidden + is-checked — mirrors swatches-list.tsx class logic
    swatchesWithData.forEach(({ li, input, isChecked }) => {
      li.classList.toggle('hidden', !visibleSet.has(li));
      li.classList.toggle('is-checked', isChecked);
      if (input) {
        input.classList.toggle('is-checked', isChecked);
        input.closest('.color-selector')?.classList.toggle('is-checked', isChecked);
        if (isChecked) {
          input.setAttribute('checked', 'true');
        } else {
          input.removeAttribute('checked');
        }
      }
    });

    // Create +X badge (Liquid no longer renders one)
    if (remaining > 0) {
      const badge = document.createElement('li');
      badge.className = 'o-style-option__list-item';
      badge.innerHTML = `
        <span class="c-product-style-list__option o-style-option__option o-style-option__option--is-available o-style-option__option--is-decorative color-swatch--button">
          <div class="o-style-option__wrap-style">
            <span class="c-product-tile__swatch-image c-product-tile__swatch-image--more fixed-utility-s">+${remaining}</span>
          </div>
        </span>
      `;
      ul.appendChild(badge);
    }
  }

  /**
   * Update card when swatch changes
   */
  updateCard(productData) {
    if (!productData) {
      return;
    }

    try {
      this.updateImage(productData);
      this.updateLink(productData);

      if (this.hasCompanionProducts()) {
        this.updateTitle(productData);
        this.updatePrice(productData);
        this.updateLabels(productData);
      }
    } catch (error) {
      console.error('Error updating card:', error);
    }
  }

  /**
   * Update product image
   */
  updateImage(productData) {
    const productImage = this.querySelector(this.CONSTANTS.SELECTORS.PRODUCT_IMAGE);
    if (!productImage) {
      return;
    }

    const featuredImage = productData.featuredImage || productData.image || productData.images?.[0];
    if (!featuredImage) {
      return;
    }

    const imageUrl = featuredImage.url || featuredImage.src || featuredImage;
    const oldSrc = productImage.src;

    productImage.src = imageUrl;
    const newSrcset = `${imageUrl}&width=750 1x, ${imageUrl}&width=1500 2x, ${imageUrl}&width=2250 3x, ${imageUrl}&width=3000 4x`;
    productImage.srcset = newSrcset;
    productImage.alt = featuredImage.altText || featuredImage.alt || productData.title;

    // Update any other images in the card
    const allImages = this.querySelectorAll('img');
    allImages.forEach((img) => {
      if (img !== productImage && img.src === oldSrc) {
        img.src = imageUrl;
        img.srcset = newSrcset;
        img.alt = featuredImage.altText || featuredImage.alt || productData.title;
      }
    });
  }

  /**
   * Update product title
   */
  updateTitle(productData) {
    const titleElement = this.querySelector(this.CONSTANTS.SELECTORS.PRODUCT_TITLE);
    if (!titleElement || !productData.title) {
      return;
    }

    titleElement.textContent = productData.title;
  }

  /**
   * Update product link URL
   */
  updateLink(productData) {
    const productLink = this.querySelector(this.CONSTANTS.SELECTORS.PRODUCT_LINK);
    if (!productLink || !productData.handle) {
      return;
    }

    productLink.href = `${window.Shopify.routes.root}products/${productData.handle}`;
  }

  /**
   * Update product price display
   */
  updatePrice(productData) {
    let priceContainer = this.querySelector(
      `${this.CONSTANTS.SELECTORS.PRICE_LABEL_CONTAINER} ${this.CONSTANTS.SELECTORS.PRICE_CONTAINER}`,
    );

    if (!priceContainer) {
      priceContainer = this.querySelector(this.CONSTANTS.SELECTORS.PRICE_CONTAINER);
    }

    // Create price container if it doesn't exist
    if (!priceContainer) {
      const productInfo = this.querySelector('.c-product-tile-info');
      if (productInfo) {
        const priceItemContainer = document.createElement('div');
        priceItemContainer.className = 'c-product-tile-info__item c-product-tile-info__item--price';

        priceContainer = document.createElement('div');
        priceContainer.className = 'c-product-price c-product-tile__product-price';

        priceItemContainer.appendChild(priceContainer);

        const productTitle = this.querySelector('.c-product-tile-info__item--name');
        if (productTitle) {
          productTitle.insertAdjacentElement('afterend', priceItemContainer);
        } else {
          productInfo.appendChild(priceItemContainer);
        }
      } else {
        return;
      }
    }

    const price = productData.price || productData.priceRange?.minVariantPrice?.amount || productData.priceRange?.min;
    const compareAtPrice =
      productData.compareAtPrice ||
      productData.compareAtPriceRange?.minVariantPrice?.amount ||
      productData.compareAtPriceRange?.min;

    if (!price) {
      return;
    }

    const formattedPrice = this.formatPrice(price);
    const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
    const hasDiscount = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);

    priceContainer.className = `c-product-price c-product-tile__product-price ${
      hasDiscount ? 'c-product-price--has-discount' : ''
    }`;

    let priceHTML;
    if (hasDiscount) {
      priceHTML = `
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="c-product-price__formatted-price fixed-utility-m">${formattedPrice}</span>
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span class="c-product-price__formatted-price c-product-price__formatted-price--discounted fixed-utility-s">${formattedComparePrice}</span>
      `;
    } else {
      priceHTML = `<span class="c-product-price__formatted-price fixed-utility-m">${formattedPrice}</span>`;
    }

    priceContainer.innerHTML = priceHTML;
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    const priceValue = typeof price === 'string' ? parseFloat(price) : price;
    const dollars = priceValue >= 100 ? priceValue / 100 : priceValue;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Update both price and product labels
   */
  updateLabels(productData) {
    if (!this.hasCompanionProducts()) {
      return;
    }

    const priceLabel = this.determinePriceLabel(productData);
    this.updatePriceLabel(productData, priceLabel);
    this.updateProductLabel(productData);
  }

  /**
   * Check if this product card has companion products
   */
  hasCompanionProducts() {
    return this.querySelectorAll(this.swatchDataClass).length > 1;
  }

  /**
   * Ensure basic DOM structure exists
   */
  ensureBasicStructure() {
    let productInfo = this.querySelector('.c-product-tile-info');
    if (!productInfo) {
      const productTile = this.querySelector('.c-product-tile');
      if (productTile) {
        productInfo = document.createElement('div');
        productInfo.className = 'c-product-tile-info c-product-tile__product-info';
        productTile.appendChild(productInfo);
      }
    }
    return !!productInfo;
  }

  /**
   * Force update labels
   */
  forceUpdateLabels(productData = null) {
    if (!productData) {
      const currentProductId = this.dataset.productId;
      productData = this.productData.get(currentProductId);
    }

    if (!productData) {
      return;
    }

    this.ensureBasicStructure();

    const priceLabel = this.determinePriceLabel(productData);
    this.updatePriceLabel(productData, priceLabel);
    this.updateProductLabel(productData);
  }

  /**
   * Determine what price label should be shown
   */
  determinePriceLabel(productData) {
    const flags = productData.flags || this.transformMetafieldsToFlags(productData.metafields);
    const country = window.Shopify?.country || 'US';
    const locale = window.localeCode?.toLowerCase() || 'en';

    let promotionalPriceMessage;
    if (country === 'CA' && locale === 'fr-ca') {
      promotionalPriceMessage = flags?.promo_applied_message_ca_fr || flags?.promotional_message_ca_fr;
    } else if (country === 'CA') {
      promotionalPriceMessage = flags?.promo_applied_message_ca_en || flags?.promotional_message_ca_en;
    } else {
      promotionalPriceMessage = flags?.promo_applied_message_us_en || flags?.promotional_message_us_en;
    }

    if (!promotionalPriceMessage) {
      promotionalPriceMessage = flags?.promotionalPriceMessage;
    }

    let crewExclusivePrice = 0;
    const crewPriceField = flags?.[`crewExclusivePrice${country}`] || flags?.crewExclusivePrice;

    if (crewPriceField) {
      try {
        const parsed = JSON.parse(crewPriceField);
        crewExclusivePrice = parseFloat(parsed.amount || 0);
      } catch {
        crewExclusivePrice = parseFloat(crewPriceField);
      }
    }

    const hasCrewPrice = crewExclusivePrice > 0;

    if (promotionalPriceMessage) {
      return promotionalPriceMessage;
    } else if (hasCrewPrice) {
      return this.translations.membersOnlyPrice;
    }

    return '';
  }

  /**
   * Update price label
   */
  updatePriceLabel(productData, priceLabelText = null) {
    let priceLabelElement = this.querySelector(this.CONSTANTS.SELECTORS.PRICE_LABEL);

    const flags = productData.flags || this.transformMetafieldsToFlags(productData.metafields);
    const t = this.translations;
    let priceLabel = priceLabelText || '';
    let labelColorClass = 'c-product-text-color--loyalty';

    const country = window.Shopify?.country || 'US';
    const locale = window.localeCode?.toLowerCase() || 'en';

    let promotionalPriceMessage;
    if (country === 'CA' && locale === 'fr-ca') {
      promotionalPriceMessage = flags?.promo_applied_message_ca_fr || flags?.promotional_message_ca_fr;
    } else if (country === 'CA') {
      promotionalPriceMessage = flags?.promo_applied_message_ca_en || flags?.promotional_message_ca_en;
    } else {
      promotionalPriceMessage = flags?.promo_applied_message_us_en || flags?.promotional_message_us_en;
    }

    if (!promotionalPriceMessage) {
      promotionalPriceMessage = flags?.promotionalPriceMessage;
    }

    let crewExclusivePrice = 0;
    const crewPriceField = flags?.[`crewExclusivePrice${country}`] || flags?.crewExclusivePrice;

    if (crewPriceField) {
      try {
        const parsed = JSON.parse(crewPriceField);
        crewExclusivePrice = parseFloat(parsed.amount || 0);
      } catch {
        crewExclusivePrice = parseFloat(crewPriceField);
      }
    }

    const hasCrewPrice = crewExclusivePrice > 0;

    if (promotionalPriceMessage) {
      priceLabel = promotionalPriceMessage;
      labelColorClass = 'c-product-text-color--urgency';
    } else if (hasCrewPrice) {
      priceLabel = t.membersOnlyPrice;
      labelColorClass = 'c-product-text-color--loyalty';
    }

    // Create price label element if it doesn't exist
    if (!priceLabelElement && priceLabel) {
      const priceContainer = this.querySelector(this.CONSTANTS.SELECTORS.PRICE_LABEL_CONTAINER);
      const productInfo = this.querySelector('.c-product-tile-info');

      if (priceContainer && productInfo) {
        priceLabelElement = document.createElement('div');
        priceLabelElement.className = 'c-product-tile__price-label';

        // Insert after price container but before swatches
        const swatchesContainer = this.querySelector('.c-color-swatches-wrapper');
        if (swatchesContainer) {
          productInfo.insertBefore(priceLabelElement, swatchesContainer);
        } else {
          const productLabelContainer = this.querySelector('.c-product-tile__product-label');
          if (productLabelContainer) {
            productInfo.insertBefore(priceLabelElement, productLabelContainer);
          } else {
            priceContainer.insertAdjacentElement('afterend', priceLabelElement);
          }
        }
      }
    }

    // Update price label content
    if (priceLabelElement) {
      if (priceLabel) {
        const contentSpan = priceLabelElement.querySelector('.c-product-tile__price-label-content');
        if (contentSpan) {
          contentSpan.textContent = priceLabel;
          contentSpan.className = `c-product-tile__price-label-content fixed-utility-s ${labelColorClass}`;
        } else {
          priceLabelElement.innerHTML = `<span class="c-product-tile__price-label-content fixed-utility-s ${labelColorClass}">${priceLabel}</span>`;
        }
      } else {
        priceLabelElement.innerHTML = '';
      }
    }

    this.priceLabel = priceLabel;
  }

  /**
   * Update product label
   */
  updateProductLabel(productData) {
    let labelElement = this.querySelector(this.CONSTANTS.SELECTORS.PRODUCT_LABEL);

    // Create product label element if it doesn't exist
    if (!labelElement) {
      const productInfo = this.querySelector('.c-product-tile-info');
      if (productInfo) {
        labelElement = document.createElement('div');
        labelElement.className = 'c-product-tile__product-label';
        productInfo.appendChild(labelElement);
      } else {
        return;
      }
    }

    // Only show one label per tile
    if (this.priceLabel) {
      labelElement.innerHTML = '';
      return;
    }

    const labelInfo = this.determineLabelPriority(productData);

    if (labelInfo.label) {
      labelElement.innerHTML = `<span class="${this.CONSTANTS.CLASSES.LABEL_CONTENT} ${labelInfo.colorClass}">${labelInfo.label}</span>`;
    } else {
      labelElement.innerHTML = '';
    }
  }

  /**
   * Transform metafields array into flags object
   */
  transformMetafieldsToFlags(metafields) {
    if (!Array.isArray(metafields)) return {};

    const flags = {};
    metafields.forEach((field) => {
      if (field?.key && field.value !== null) {
        flags[field.key] = field.value;
      }
    });

    return flags;
  }

  /**
   * Determine label priority
   * Matches the priority order from shared-product-card-label.liquid
   */
  determineLabelPriority(productData) {
    if (!productData) {
      return { label: '', colorClass: '' };
    }

    const flags = productData.flags || this.transformMetafieldsToFlags(productData.metafields);
    const availableForSale = productData.availableForSale ?? true;
    const t = this.translations;
    const country = window.Shopify?.country || 'US';
    const locale = window.localeCode?.toLowerCase() || 'en';

    let label = '';
    let labelClass = this.CONSTANTS.COLORS.REGULAR;

    // Check tags for excluded from promotions
    const tags = productData.tags || [];
    const tagsLower = tags.map((tag) => tag.toLowerCase());
    const hasNotify = tagsLower.some((tag) => tag.includes('notify'));
    const hasExcludedFromPromotions = tagsLower.some((tag) => tag.includes('excludedfrompromotions'));

    // Region-specific flags (handle both key formats and boolean/string conversion)
    const getBooleanFlag = (flagValue) => {
      if (flagValue === true || flagValue === 'true') return true;
      if (flagValue === false || flagValue === 'false') return false;
      return !!flagValue;
    };

    const isPreorder = getBooleanFlag(
      country === 'US' ? flags?.preOrderUS || flags?.preOrder : flags?.preOrderCA || flags?.preOrder,
    );
    const isComingSoon = getBooleanFlag(
      country === 'US' ? flags?.comingSoonUS || flags?.comingSoon : flags?.comingSoonCA || flags?.comingSoon,
    );
    const isLoyaltyEarlyBuy = getBooleanFlag(
      country === 'US'
        ? flags?.loyaltyEarlyBuyUS || flags?.loyaltyEarlyBuy
        : flags?.loyaltyEarlyBuyCA || flags?.loyaltyEarlyBuy,
    );
    const loyaltyEarlyBuyEndDate =
      country === 'US'
        ? flags?.loyaltyEarlyBuyEndDateUS || flags?.loyaltyEarlyBuyEndDate
        : flags?.loyaltyEarlyBuyEndDateCA || flags?.loyaltyEarlyBuyEndDate;

    // Check if loyalty early buy is valid (not expired)
    let isLoyaltyEarlyBuyValid = false;
    if (isLoyaltyEarlyBuy && loyaltyEarlyBuyEndDate) {
      try {
        const endDateTimestamp = new Date(loyaltyEarlyBuyEndDate).getTime() / 1000;
        const nowTimestamp = Date.now() / 1000;
        if (endDateTimestamp > nowTimestamp) {
          isLoyaltyEarlyBuyValid = true;
        }
      } catch {
        // Invalid date, skip
      }
    }

    // Online exclusive check (handle both boolean and string formats)
    const onlineExclusiveRaw = flags?.onlineExclusive;
    const onlineExclusive =
      onlineExclusiveRaw === true ||
      onlineExclusiveRaw === 'true' ||
      (onlineExclusiveRaw && onlineExclusiveRaw !== '' && onlineExclusiveRaw !== 'false');

    // Promotional message check (handle both key formats)
    let promotionalMessage;
    if (country === 'CA' && locale === 'fr-ca') {
      promotionalMessage = flags?.promotional_message_ca_fr || flags?.promotionalMessageCAFR || '';
    } else if (country === 'CA') {
      promotionalMessage = flags?.promotional_message_ca_en || flags?.promotionalMessageCAEN || '';
    } else {
      promotionalMessage = flags?.promotional_message_us_en || flags?.promotionalMessageUSEN || '';
    }

    // Check if not special tagged (no preorder, no coming soon, no excluded from promotions)
    const isNotSpecialTagged = !isPreorder && !isComingSoon && !hasExcludedFromPromotions;

    // Check if truly neutral (not special tagged, no early buy, not online exclusive, no promotional message)
    const isTrulyNeutral = isNotSpecialTagged && !isLoyaltyEarlyBuyValid && !onlineExclusive && !promotionalMessage;

    // Check for new arrival (handle both key formats and boolean/string conversion)
    const isNewArrivalUS = getBooleanFlag(flags?.isNewArrivalUS || flags?.newArrivalUS || flags?.isNewArrival || false);
    const isNewArrivalCA = getBooleanFlag(flags?.isNewArrivalCA || flags?.newArrivalCA || flags?.isNewArrival || false);
    let isNewArrival = country === 'US' ? isNewArrivalUS : isNewArrivalCA;

    // Fallback: calculate from publish date if flags not available
    if (!isNewArrival) {
      const publishDateKey = country === 'US' ? 'publishDateUS' : 'publishDateCA';
      const publishDate = flags?.[publishDateKey];
      if (publishDate) {
        try {
          const publishTimestamp = new Date(publishDate).getTime() / 1000;
          const nowTimestamp = Date.now() / 1000;
          const daysSincePublish = (nowTimestamp - publishTimestamp) / (60 * 60 * 24);
          const newProductDays = this.CONSTANTS.THRESHOLDS.NEW_PRODUCT_DAYS || 60;
          isNewArrival = daysSincePublish <= newProductDays && daysSincePublish >= 0;
        } catch {
          // Invalid date, skip new arrival check
        }
      }
    }

    // Check for low stock (matches Liquid: variant.inventory_quantity != null and > 0 and <= 5)
    // Note: Using totalInventory since GraphQL query doesn't fetch variant-level inventory
    // In product cards with swatches, each swatch is a different product, so totalInventory
    // represents the inventory for that specific color variant product
    const isLowStock =
      (productData.totalInventory != null &&
        typeof productData.totalInventory === 'number' &&
        productData.totalInventory > 0 &&
        productData.totalInventory <= 5) ||
      (productData.selectedOrFirstAvailableVariant?.quantityAvailable > 0 &&
        productData.selectedOrFirstAvailableVariant?.quantityAvailable <= 5);

    // Check for non-returnable (Last Chance) - handle both key formats
    const nonReturnableType = flags?.nonReturnableType || flags?.non_returnable_type;
    const isNonReturnable =
      nonReturnableType &&
      typeof nonReturnableType === 'string' &&
      nonReturnableType.toLowerCase() !== 'hygiene' &&
      nonReturnableType.trim() !== '';

    // Check if price label exists (affects which labels can show)
    const hasPriceLabel = !!this.priceLabel;

    // Priority 1: Sold Out
    if (!availableForSale && !hasNotify && !isComingSoon && !isPreorder && !hasPriceLabel) {
      label = t.soldOut;
      labelClass = this.CONSTANTS.COLORS.URGENCY;
    }
    // Priority 2: Preorder
    else if (availableForSale && isPreorder && !hasPriceLabel) {
      label = t.preorder;
      labelClass = this.CONSTANTS.COLORS.REGULAR;
    }
    // Priority 3: Early Access
    else if (availableForSale && !isPreorder && isLoyaltyEarlyBuyValid && !hasPriceLabel) {
      label = t.earlyAccess;
      labelClass = this.CONSTANTS.COLORS.LOYALTY;
    }
    // Priority 4: Coming Soon
    else if (availableForSale && !isPreorder && !isLoyaltyEarlyBuyValid && isComingSoon && !hasPriceLabel) {
      label = t.comingSoon;
      labelClass = this.CONSTANTS.COLORS.LOYALTY;
    }
    // Priority 5: Excluded from Promotions
    else if (
      availableForSale &&
      !isPreorder &&
      !isComingSoon &&
      !isLoyaltyEarlyBuyValid &&
      hasExcludedFromPromotions &&
      !hasPriceLabel
    ) {
      label = t.noPromo;
      labelClass = this.CONSTANTS.COLORS.REGULAR;
    }
    // Priority 6: Online Exclusive
    else if (availableForSale && isNotSpecialTagged && !isLoyaltyEarlyBuyValid && onlineExclusive && !hasPriceLabel) {
      label = t.onlineExclusive;
      labelClass = this.CONSTANTS.COLORS.REGULAR;
    }
    // Priority 7: Promotional Message (can show even when price label exists)
    else if (
      availableForSale &&
      isNotSpecialTagged &&
      !isLoyaltyEarlyBuyValid &&
      !onlineExclusive &&
      promotionalMessage
    ) {
      label = promotionalMessage;
      labelClass = this.CONSTANTS.COLORS.REGULAR;
    }
    // Priority 8: New Arrival
    else if (availableForSale && isTrulyNeutral && isNewArrival && !hasPriceLabel) {
      label = t.new;
      labelClass = this.CONSTANTS.COLORS.REGULAR;
    }
    // Priority 9: Low Stock
    else if (availableForSale && isTrulyNeutral && !isNewArrival && isLowStock && !hasPriceLabel) {
      label = t.lowStock;
      labelClass = this.CONSTANTS.COLORS.URGENCY;
    }
    // Priority 10: Last Chance (Non-Returnable)
    else if (availableForSale && isTrulyNeutral && !isNewArrival && !isLowStock && isNonReturnable && !hasPriceLabel) {
      label = t.lastChance;
      labelClass = this.CONSTANTS.COLORS.URGENCY;
    }

    return { label, colorClass: labelClass };
  }

  /**
   * Set up accessibility attributes
   */
  setupAccessibility() {
    const swatches = this.querySelectorAll(this.swatchDataClass);
    swatches.forEach((swatch) => {
      if (!swatch.getAttribute('aria-label')) {
        const colorLabel = swatch.dataset.colorLabel || 'Color option';
      }
    });
  }

  /**
   * Debounce utility
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register the custom element
customElements.define('shared-product-card', ProductCardElement);

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCardElement;
}
